import { apiClient } from './apiConfig';

export interface LoginResponse {
  success: boolean;
  user?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_staff: boolean;
    is_superuser: boolean;
  };
  access_token?: string;
  refresh_token?: string;
  error?: string;
}

export interface CurrentUserResponse {
  success: boolean;
  user?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_staff: boolean;
    is_superuser: boolean;
  };
}

export interface TokenRefreshResponse {
  success: boolean;
  access_token?: string;
  error?: string;
}

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

// JWT Token management
const TOKEN_STORAGE_KEY = 'floos_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'floos_refresh_token';
const USER_STORAGE_KEY = 'floos_user';

// Store authentication state
let currentUser: LoginResponse['user'] | null = null;
let accessToken: string | null = null;
let refreshToken: string | null = null;

/**
 * Initialize tokens from localStorage on app start
 */
function initializeTokens(): void {
  try {
    accessToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);
    
    if (storedUser) {
      currentUser = JSON.parse(storedUser);
    }
    
  } catch (error) {
    console.error('[Auth] Error initializing tokens:', error);
    clearTokens();
  }
}

/**
 * Store tokens in localStorage
 */
function storeTokens(access: string, refresh: string, user: LoginResponse['user']): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    
    accessToken = access;
    refreshToken = refresh;
    currentUser = user;
    
  } catch (error) {
    console.error('[Auth] Error storing tokens:', error);
  }
}

/**
 * Clear all stored tokens
 */
function clearTokens(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    
    accessToken = null;
    refreshToken = null;
    currentUser = null;
    
  } catch (error) {
    console.error('[Auth] Error clearing tokens:', error);
  }
}

/**
 * Login with username and password using JWT authentication
 * Only allows superuser access
 */
export async function login(username: string, password: string): Promise<LoginResponse> {
  try {
    
    // Make login request to JWT endpoint
    const response = await apiClient.post('/api/auth/login/', {
      username,
      password
    });
    
    if (response.data.success && response.data.access_token) {
      const { user, access_token, refresh_token } = response.data;
      
      // Verify user is superuser
      if (!user.is_superuser) {
        console.error('[Auth] Access denied: User is not a superuser');
        return {
          success: false,
          error: 'Access denied: Only superusers can access this application'
        };
      }
      
      // Store tokens and user data
      storeTokens(access_token, refresh_token, user);
      
      return {
        success: true,
        user: user,
        access_token,
        refresh_token
      };
    } else {
      return {
        success: false,
        error: response.data.error || 'Authentication failed'
      };
    }
  } catch (error: any) {
    console.error('[Auth] Login error:', error);
    
    if (error.response?.status === 401) {
      return {
        success: false,
        error: 'Invalid username or password'
      };
    } else if (error.response?.status === 403) {
      return {
        success: false,
        error: 'Access denied: Only superusers can access this application'
      };
    }
    
    return {
      success: false,
      error: error.response?.data?.error || 'Login failed. Please try again.'
    };
  }
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(): Promise<TokenRefreshResponse> {
  try {
    if (!refreshToken) {
      return { success: false, error: 'No refresh token available' };
    }
    
    
    const response = await apiClient.post('/api/auth/refresh/', {
      refresh: refreshToken
    });
    
    if (response.data.success && response.data.access_token) {
      const newAccessToken = response.data.access_token;
      
      // Update stored access token
      localStorage.setItem(TOKEN_STORAGE_KEY, newAccessToken);
      accessToken = newAccessToken;
      
      return {
        success: true,
        access_token: newAccessToken
      };
    } else {
      clearTokens();
      return {
        success: false,
        error: response.data.error || 'Token refresh failed'
      };
    }
  } catch (error: any) {
    console.error('[Auth] Token refresh error:', error);
    clearTokens();
    return {
      success: false,
      error: 'Token refresh failed'
    };
  }
}

/**
 * Get current user information using JWT authentication
 */
export async function getCurrentUser(): Promise<CurrentUserResponse> {
  try {
    
    // Initialize tokens if not already done
    if (!accessToken && !refreshToken) {
      initializeTokens();
    }
    
    // If we have a cached user and valid token, return it
    if (currentUser && accessToken) {
      return {
        success: true,
        user: currentUser
      };
    }
    
    // If no access token, try to refresh
    if (!accessToken && refreshToken) {
      const refreshResult = await refreshAccessToken();
      if (!refreshResult.success) {
        return { success: false };
      }
    }
    
    // If still no access token, user needs to login
    if (!accessToken) {
      return { success: false };
    }
    
    // Make request to get current user
    const response = await apiClient.get('/api/auth/current-user/');
    
    if (response.data.success && response.data.user) {
      const user = response.data.user;
      
      // Verify user is still a superuser
      if (!user.is_superuser) {
        console.error('[Auth] Access denied: User is no longer a superuser');
        clearTokens();
        return { success: false };
      }
      
      // Update cached user
      currentUser = user;
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      
      return {
        success: true,
        user: user
      };
    } else {
      clearTokens();
      return { success: false };
    }
    
  } catch (error: any) {
    console.error('[Auth] Get current user error:', error);
    
    // If it's an authentication error, try to refresh token
    if (error.response?.status === 401 && refreshToken) {
      const refreshResult = await refreshAccessToken();
      
      if (refreshResult.success) {
        // Retry getting current user with new token
        try {
          const response = await apiClient.get('/api/auth/current-user/');
          if (response.data.success && response.data.user) {
            const user = response.data.user;
            currentUser = user;
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
            return {
              success: true,
              user: user
            };
          }
        } catch (retryError) {
          console.error('[Auth] Retry failed:', retryError);
        }
      }
    }
    
    // Clear tokens on any authentication failure
    clearTokens();
    return { success: false };
  }
}

/**
 * Logout the current user using JWT authentication
 * Blacklists the refresh token and clears all stored data
 */
export async function logout(): Promise<void> {
  try {
    
    // Step 1: Call backend logout endpoint to blacklist refresh token
    if (refreshToken) {
      try {
        await apiClient.post('/api/auth/logout/', {
          refresh: refreshToken
        });
      } catch (error) {
      }
    }
    
    // Step 2: Clear all stored tokens and user data
    clearTokens();
    
    // Step 3: Clear all browser storage and caches
    clearAllStorageAndCache();
    
  } catch (error) {
    console.error('[Auth] Logout error:', error);
    // Even if logout fails, clear local data
    clearTokens();
    clearAllStorageAndCache();
  }
}

/**
 * Clear all storage and cache - Enhanced for Basic Auth logout
 */
export function clearAllStorageAndCache(): void {
  try {
    
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear IndexedDB if available
    if ('indexedDB' in window) {
      try {
        indexedDB.databases?.()?.then(databases => {
          databases.forEach(db => {
            if (db.name) {
              indexedDB.deleteDatabase(db.name);
            }
          });
        });
      } catch (error) {
      }
    }
    
    // Clear service worker caches
    if ('caches' in window) {
      caches.keys().then(keys => {
        keys.forEach(key => caches.delete(key));
      });
    }
    
    // Clear cookies (if we have any)
    if (document.cookie) {
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      });
    }
    
    // Clear any potential credential managers
    if ('credentials' in navigator) {
      try {
        // This might not work in all browsers, but it's worth trying
        (navigator as any).credentials?.preventSilentAccess?.();
      } catch (error) {
      }
    }
    
  } catch (error) {
    console.error('[Auth] Error clearing data:', error);
  }
}

/**
 * Get current access token
 */
export function getAccessToken(): string | null {
  if (!accessToken) {
    initializeTokens();
  }
  return accessToken;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  if (!accessToken && !refreshToken) {
    initializeTokens();
  }
  return !!(currentUser && accessToken);
}

/**
 * Initialize authentication on app start
 */
export function initializeAuth(): void {
  initializeTokens();
}

/**
 * Test backend connectivity
 */
export async function testBackendConnectivity(): Promise<boolean> {
  try {
    const response = await apiClient.get('/api/health/');
    return true;
  } catch (error) {
    console.error('[Auth] Backend connectivity test failed:', error);
    return false;
  }
}

// Debug functions for development
export function debugClearAllData(): void {
  clearAllStorageAndCache();
  clearTokens();
}

// Make debug functions available globally in development
if (isDevelopment) {
  (window as any).clearAuthCache = debugClearAllData;
  (window as any).testLogout = logout;
  (window as any).getTokens = () => ({ accessToken, refreshToken, currentUser });
}

// Initialize authentication when module loads
initializeAuth(); 